import { Job, Notification, useNotificationsQuery, useUpdateNotificationStatusMutation, useExportJobsQuery } from "src/context/api/data/api";
import { useCallback, useEffect, useRef } from "react";
import { enqueueSnackbar } from "notistack";
import { deriveNotificationView } from "./notifications.utils";
import { hasActiveExportJob, splitExportJobs } from "./exports.helpers";

// Pure helper: poll faster while an export job is still in flight, slower otherwise.
export function pickPollInterval(hasActiveJob: boolean): number {
    return hasActiveJob ? 5000 : 60000;
}

export default function useNotifications() {
    // Export jobs, to detect when one becomes active/inactive. The popover
    // polls the same query every 5s, and RTK keeps the shortest interval.
    const { data: exportJobs } = useExportJobsQuery(undefined, { pollingInterval: 15 * 1000 });
    const hasActiveJob = hasActiveExportJob(exportJobs?.jobs);
    const prevExportJobs = useRef<Job[] | null>(null);

    // Adaptive polling: faster while a job is active, slower otherwise.
    const { data: notifications = null, isLoading: isLoadingNotifications, isFetching: isFetchingNotifications, refetch } =
        useNotificationsQuery(undefined, { pollingInterval: pickPollInterval(hasActiveJob) });
    const prevNotifications = useRef<Notification[] | null>(null);
    const [updateNotificationStatus] = useUpdateNotificationStatusMutation();

    const handleUpdateNotificationStatus = useCallback((notificationId: string, status: "read" | "dismissed" | "archived") => {
        updateNotificationStatus({ notificationId, status })
            .unwrap()
            .then(() => {
                refetch();
            });
    }, [updateNotificationStatus, refetch]);

    useEffect(() => {
        if (notifications && prevNotifications.current) {
            notifications.notifications.forEach((notification) => {
                const currentNotification = prevNotifications.current?.find((prevNotification) => prevNotification.id === notification.id);
                if (!currentNotification && notification.status === "unread" && !notification.type) {
                    enqueueSnackbar(notification.title + " : \n" + notification.content, { variant: "info" });
                }
            });
        }
        prevNotifications.current = notifications?.notifications ?? null;
    }, [notifications]);

    // Export failures are never listed in the notification center: surface them
    // as an ephemeral toast the first time a job transitions into "failed".
    useEffect(() => {
        // The first list loaded is the baseline: the jobs that had already failed
        // before it are history, not news.
        if (!exportJobs) return;
        const { jobs } = exportJobs;
        if (prevExportJobs.current) {
            const { failed } = splitExportJobs(jobs);
            failed.forEach((job) => {
                const previousJob = prevExportJobs.current?.find((prev) => prev.id === job.id);
                if (!previousJob || previousJob.status !== "failed") {
                    enqueueSnackbar("Export failed. Please try again.", { variant: "error" });
                }
            });
        }
        prevExportJobs.current = jobs;
    }, [exportJobs]);

    const raw = notifications?.notifications ?? [];
    const decorated = raw.map((notification) => ({
        ...notification,
        read: () => handleUpdateNotificationStatus(notification.id, "read"),
        dismiss: () => handleUpdateNotificationStatus(notification.id, "dismissed"),
        archive: () => handleUpdateNotificationStatus(notification.id, "archived"),
    }));

    // Single source of truth: derive visibility/unread-count from the same decorated list.
    const { visible, unreadCount } = deriveNotificationView(decorated as unknown as Notification[]);

    return {
        notifications: decorated,
        visibleNotifications: visible as unknown as typeof decorated,
        unreadCount,
        isLoadingNotifications,
        isFetchingNotifications,
    };
}
